import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import CustomSnackbar from "../../components/CustomSnackbar";
import Logger from "../../../services/Logger";
import TagRenameDialog from "../../system/tag-migration/TagRenameDialog";
import { getPuzzleTypeById } from "../PuzzleTypeConstants";
import dayjs from "dayjs";

const LOW_PUBLISHED = 5;
const SORT_FIELDS = ["tag", "type", "draftCount", "reviewedCount", "publishedCount", "playCount", "lastPublishedAt"];
const DEFAULT_SORT = { field: "publishedCount", dir: "asc" };
const LANGUAGE_FILTER_OPTIONS = [
    { value: "", label: "All languages" },
    { value: "english", label: "English" },
    { value: "french", label: "French" },
];

function formatCount(value) {
    return Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—";
}

function formatDateTime(epochMillis) {
    return epochMillis ? dayjs(Number(epochMillis)).format("YYYY-MM-DD HH:mm") : "—";
}

function lastPublishedValue(row) {
    const raw = row?.lastPublishedAt;
    if (raw == null || raw === "") {
        return null;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
        return n;
    }
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.valueOf() : null;
}

function loadStoredSort(storageKey) {
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
            return DEFAULT_SORT;
        }
        const parsed = JSON.parse(raw);
        const field = SORT_FIELDS.includes(parsed?.field) ? parsed.field : DEFAULT_SORT.field;
        const dir = parsed?.dir === "desc" ? "desc" : "asc";
        return { field, dir };
    } catch {
        return DEFAULT_SORT;
    }
}

function persistSort(storageKey, field, dir) {
    try {
        window.localStorage.setItem(storageKey, JSON.stringify({ field, dir }));
    } catch {
        /* private mode */
    }
}

function typeStorageKey(sortStorageKey) {
    if (typeof sortStorageKey === "string" && sortStorageKey.endsWith(".sort")) {
        return `${sortStorageKey.slice(0, -5)}.type`;
    }
    return "admin.wordPuzzle.tagStats.type";
}

function loadTypeFilter(storageKey, options) {
    const allowed = new Set((options || []).map((o) => o.value));
    try {
        const v = window.localStorage.getItem(storageKey);
        if (allowed.has(v)) return v;
    } catch {
        /* private mode */
    }
    return allowed.has("") ? "" : (options?.[0]?.value ?? "");
}

function typeLabel(type) {
    const id = String(type || "").trim().toUpperCase();
    const known = getPuzzleTypeById(id);
    if (known?.name) return known.name;
    if (id === "HIDDEN_MESSAGE") return "Hidden Message";
    if (id === "WORD_SEARCH") return "Word Search";
    return id || "—";
}

function rowKey(row) {
    const tag = String(row?.tag || "");
    const type = String(row?.type || "");
    return type ? `${tag}::${type}` : tag;
}

function languageStorageKey(sortStorageKey) {
    if (typeof sortStorageKey === "string" && sortStorageKey.endsWith(".sort")) {
        return `${sortStorageKey.slice(0, -5)}.language`;
    }
    return "admin.wordPuzzle.tagStats.language";
}

function loadLanguageFilter(storageKey, fallback) {
    try {
        const v = window.localStorage.getItem(storageKey);
        if (v === "" || v === "english" || v === "french") return v;
    } catch {
        /* private mode */
    }
    if (fallback === "" || fallback === "english" || fallback === "french") return fallback;
    return "english";
}

/**
 * Per-tag REVIEWED vs PUBLISHED word-puzzle counts, with publish-one for thin tags.
 */
export default function WordPuzzleTagStatsPanel({
    language,
    fetchStats,
    publishReviewed,
    generateIdea,
    dashboardPath,
    puzzleLabel,
    sortStorageKey,
    typeFilterOptions,
}) {
    const navigate = useNavigate();
    const languageKey = languageStorageKey(sortStorageKey);
    const typeKey = typeStorageKey(sortStorageKey);
    const showType = Array.isArray(typeFilterOptions) && typeFilterOptions.length > 0;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState({ draftTotal: 0, reviewedTotal: 0, publishedTotal: 0, playTotal: 0 });
    const [languageFilter, setLanguageFilter] = useState(() => loadLanguageFilter(languageKey, language));
    const [typeFilter, setTypeFilter] = useState(() =>
        showType ? loadTypeFilter(typeKey, typeFilterOptions) : "",
    );
    const [keyword, setKeyword] = useState("");
    const [sort, setSort] = useState(() => loadStoredSort(sortStorageKey));
    const sortField = sort.field;
    const sortDir = sort.dir;
    const [publishTag, setPublishTag] = useState(null);
    const [publishPuzzleType, setPublishPuzzleType] = useState("");
    const [publishing, setPublishing] = useState(false);
    const [renameTag, setRenameTag] = useState(null);
    const [genIdeaTag, setGenIdeaTag] = useState(null);
    const [generatingIdea, setGeneratingIdea] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchStats(languageFilter || undefined, typeFilter || undefined);
            const list = Array.isArray(result?.tags) ? result.tags : [];
            setRows(list);
            setTotals({
                draftTotal: Number(result?.draftTotal) || 0,
                reviewedTotal: Number(result?.reviewedTotal) || 0,
                publishedTotal: Number(result?.publishedTotal) || 0,
                playTotal: Number(result?.playTotal) || 0,
            });
        } catch (err) {
            Logger.error(`Failed to load ${puzzleLabel} tag stats:`, err);
            setError("Failed to load tag statistics.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [fetchStats, languageFilter, typeFilter, puzzleLabel]);

    useEffect(() => {
        load();
    }, [load]);

    const handleSort = (field) => {
        if (!SORT_FIELDS.includes(field)) return;
        const nextDir =
            sortField === field
                ? (sortDir === "asc" ? "desc" : "asc")
                : (field === "lastPublishedAt" ? "desc" : "asc");
        setSort({ field, dir: nextDir });
        persistSort(sortStorageKey, field, nextDir);
    };

    const visibleRows = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        const filtered = q
            ? rows.filter((row) => String(row.tag || "").toLowerCase().includes(q))
            : rows.slice();
        const dir = sortDir === "asc" ? 1 : -1;
        filtered.sort((a, b) => {
            if (sortField === "tag") {
                return dir * String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                    sensitivity: "base",
                });
            }
            if (sortField === "type") {
                const cmp = dir * typeLabel(a.type).localeCompare(typeLabel(b.type), undefined, {
                    sensitivity: "base",
                });
                if (cmp !== 0) return cmp;
                return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                    sensitivity: "base",
                });
            }
            if (sortField === "lastPublishedAt") {
                const av = lastPublishedValue(a);
                const bv = lastPublishedValue(b);
                if (av == null && bv == null) {
                    return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                        sensitivity: "base",
                    });
                }
                if (av == null) return 1;
                if (bv == null) return -1;
                if (av !== bv) return dir * (av - bv);
                return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                    sensitivity: "base",
                });
            }
            const av = Number(a[sortField]) || 0;
            const bv = Number(b[sortField]) || 0;
            if (av !== bv) return dir * (av - bv);
            return String(a.tag || "").localeCompare(String(b.tag || ""), undefined, {
                sensitivity: "base",
            });
        });
        return filtered;
    }, [keyword, rows, sortDir, sortField]);

    const handlePublish = async () => {
        if (!publishTag || publishing) return;
        setPublishing(true);
        try {
            const result = await publishReviewed(
                1,
                languageFilter || undefined,
                publishTag,
                publishPuzzleType || undefined,
            );
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Publish failed",
                });
                return;
            }
            const count = result.data?.publishedCount ?? 0;
            setSnackbar({
                open: true,
                severity: count > 0 ? "success" : "warning",
                message:
                    count > 0
                        ? `Published ${count} ${puzzleLabel} for “${publishTag}”.`
                        : `No REVIEWED ${puzzleLabel} left for “${publishTag}”.`,
            });
            setPublishTag(null);
            setPublishPuzzleType("");
            await load();
        } catch (err) {
            Logger.error(`Failed to publish ${puzzleLabel} by tag:`, err);
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Publish failed",
            });
        } finally {
            setPublishing(false);
        }
    };

    const handleDashboard = (tag, puzzleType) => {
        const t = String(tag || "").trim();
        if (!t || !dashboardPath) return;
        const params = new URLSearchParams();
        params.set("tag", t);
        const type = String(puzzleType || "").trim().toUpperCase();
        if (type === "HIDDEN_MESSAGE" || type === "WORD_SEARCH") {
            params.set("type", type);
        }
        navigate(`${dashboardPath}?${params.toString()}`);
    };

    const handleGenIdea = async () => {
        if (!genIdeaTag || generatingIdea || !generateIdea) return;
        setGeneratingIdea(true);
        try {
            const result = await generateIdea(genIdeaTag);
            if (!result.success) {
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Gen Idea failed",
                });
                return;
            }
            const count = Number(result.data?.puzzlesCreated) || 0;
            const ideaTitle = String(result.data?.ideaTitle || "").trim();
            const ideaLabel = ideaTitle ? `“${ideaTitle}”` : "the latest idea";
            setSnackbar({
                open: true,
                severity: count > 0 ? "success" : "warning",
                message:
                    count > 0
                        ? `Created ${count} draft ${count === 1 ? "puzzle" : "puzzles"} from ${ideaLabel}.`
                        : `${ideaLabel} produced no new drafts.`,
            });
            setGenIdeaTag(null);
            await load();
        } catch (err) {
            Logger.error(`Failed to generate idea for ${puzzleLabel} tag:`, err);
            setSnackbar({
                open: true,
                severity: "error",
                message: err?.message || "Gen Idea failed",
            });
        } finally {
            setGeneratingIdea(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each row is one tag on a {puzzleLabel} template. <strong>Draft</strong> is still in
                progress. <strong>Reviewed</strong> is the ready-to-go-live inventory.
                <strong> Published</strong> is live in the app.
                <strong> Last published</strong> is the most recent live puzzle for that tag.
                <strong> Plays</strong> is how many user sessions have started that tag (guests
                included; test and staff excluded).
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Typography variant="body2">
                    Tags: <strong>{formatCount(rows.length)}</strong>
                </Typography>
                <Typography variant="body2">
                    Draft: <strong>{formatCount(totals.draftTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Reviewed: <strong>{formatCount(totals.reviewedTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Published: <strong>{formatCount(totals.publishedTotal)}</strong>
                </Typography>
                <Typography variant="body2">
                    Plays: <strong>{formatCount(totals.playTotal)}</strong>
                </Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap alignItems="center">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id={`${puzzleLabel}-tag-stats-language-label`}>Language</InputLabel>
                    <Select
                        labelId={`${puzzleLabel}-tag-stats-language-label`}
                        value={languageFilter}
                        label="Language"
                        onChange={(event) => {
                            const next = event.target.value;
                            setLanguageFilter(next);
                            try {
                                window.localStorage.setItem(languageKey, next);
                            } catch {
                                /* private mode */
                            }
                        }}
                    >
                        {LANGUAGE_FILTER_OPTIONS.map((option) => (
                            <MenuItem key={option.value || "all"} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {showType ? (
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id={`${puzzleLabel}-tag-stats-type-label`}>Type</InputLabel>
                        <Select
                            labelId={`${puzzleLabel}-tag-stats-type-label`}
                            value={typeFilter}
                            label="Type"
                            onChange={(event) => {
                                const next = event.target.value;
                                setTypeFilter(next);
                                try {
                                    window.localStorage.setItem(typeKey, next);
                                } catch {
                                    /* private mode */
                                }
                            }}
                        >
                            {typeFilterOptions.map((option) => (
                                <MenuItem key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : null}
                <TextField
                    size="small"
                    label="Filter tags"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    sx={{ maxWidth: 320, flex: "1 1 220px" }}
                />
            </Stack>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === "tag"}
                                    direction={sortField === "tag" ? sortDir : "asc"}
                                    onClick={() => handleSort("tag")}
                                >
                                    Tag
                                </TableSortLabel>
                            </TableCell>
                            {showType ? (
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === "type"}
                                        direction={sortField === "type" ? sortDir : "asc"}
                                        onClick={() => handleSort("type")}
                                    >
                                        Type
                                    </TableSortLabel>
                                </TableCell>
                            ) : null}
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "draftCount"}
                                    direction={sortField === "draftCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("draftCount")}
                                >
                                    Draft
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "reviewedCount"}
                                    direction={sortField === "reviewedCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("reviewedCount")}
                                >
                                    Reviewed
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "publishedCount"}
                                    direction={sortField === "publishedCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("publishedCount")}
                                >
                                    Published
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                                <TableSortLabel
                                    active={sortField === "playCount"}
                                    direction={sortField === "playCount" ? sortDir : "asc"}
                                    onClick={() => handleSort("playCount")}
                                >
                                    Plays
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === "lastPublishedAt"}
                                    direction={sortField === "lastPublishedAt" ? sortDir : "desc"}
                                    onClick={() => handleSort("lastPublishedAt")}
                                >
                                    Last published
                                </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={showType ? 8 : 7}>
                                    <Typography variant="body2" color="text.secondary">
                                        No tags found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            visibleRows.map((row) => {
                                const draft = Number(row.draftCount) || 0;
                                const reviewed = Number(row.reviewedCount) || 0;
                                const published = Number(row.publishedCount) || 0;
                                const plays = Number(row.playCount) || 0;
                                const low = published < LOW_PUBLISHED;
                                return (
                                    <TableRow key={rowKey(row)} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {row.tag}
                                            </Typography>
                                        </TableCell>
                                        {showType ? (
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={typeLabel(row.type)}
                                                    icon={
                                                        getPuzzleTypeById(row.type)?.icon ? (
                                                            <span>{getPuzzleTypeById(row.type).icon}</span>
                                                        ) : undefined
                                                    }
                                                />
                                            </TableCell>
                                        ) : null}
                                        <TableCell align="right">{formatCount(draft)}</TableCell>
                                        <TableCell align="right">{formatCount(reviewed)}</TableCell>
                                        <TableCell align="right">
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                                alignItems="center"
                                            >
                                                <span>{formatCount(published)}</span>
                                                {low ? (
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                        label="Low"
                                                    />
                                                ) : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">{formatCount(plays)}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatDateTime(lastPublishedValue(row))}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                                alignItems="center"
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                {dashboardPath ? (
                                                    <Tooltip title="Dashboard">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleDashboard(row.tag, row.type)}
                                                            aria-label={`Open ${puzzleLabel} board for ${row.tag}`}
                                                        >
                                                            <DashboardOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : null}
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={reviewed < 1}
                                                    onClick={() => {
                                                        setPublishTag(row.tag);
                                                        setPublishPuzzleType(row.type || typeFilter || "");
                                                    }}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    Publish
                                                </Button>
                                                {typeof generateIdea === "function" ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={generatingIdea}
                                                        onClick={() => setGenIdeaTag(row.tag)}
                                                        sx={{ textTransform: "none" }}
                                                    >
                                                        Gen Idea
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    size="small"
                                                    onClick={() => setRenameTag(row.tag)}
                                                    sx={{ textTransform: "none" }}
                                                >
                                                    Rename tag
                                                </Button>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={Boolean(publishTag)}
                onClose={() => {
                    if (publishing) return;
                    setPublishTag(null);
                    setPublishPuzzleType("");
                }}
            >
                <DialogTitle>Publish {puzzleLabel}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Publish 1 REVIEWED {puzzleLabel}
                        {publishPuzzleType ? ` (${typeLabel(publishPuzzleType)})` : ""} for tag{" "}
                        <strong>{publishTag}</strong>? The newest-created puzzle is published first.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setPublishTag(null);
                            setPublishPuzzleType("");
                        }}
                        disabled={publishing}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handlePublish} disabled={publishing} variant="contained">
                        {publishing ? "Publishing…" : "Publish"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(genIdeaTag)}
                onClose={() => !generatingIdea && setGenIdeaTag(null)}
            >
                <DialogTitle>Gen Idea</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Generate draft {puzzleLabel} puzzles from a Bedrock idea tagged{" "}
                        <strong>{genIdeaTag}</strong>? Uses an idea that already created {puzzleLabel}{" "}
                        puzzles for this tag. Enabled can be off.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGenIdeaTag(null)} disabled={generatingIdea}>
                        Cancel
                    </Button>
                    <Button onClick={handleGenIdea} disabled={generatingIdea} variant="contained">
                        {generatingIdea ? "Generating…" : "Gen Idea"}
                    </Button>
                </DialogActions>
            </Dialog>

            <TagRenameDialog
                open={Boolean(renameTag)}
                onClose={() => setRenameTag(null)}
                initialFromTag={renameTag || ""}
                language={languageFilter}
                onMessage={(msg) => setSnackbar({ open: true, ...msg })}
                onRenamed={() => load()}
            />

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
